import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Loader2, UserPlus, UserCheck, UserMinus } from "lucide-react";
import { cn } from "@/lib/utils";

interface FollowButtonProps {
  targetUserId: string;
  size?: "default" | "sm" | "lg" | "icon";
  variant?: "default" | "outline" | "ghost" | "secondary";
  className?: string;
  showText?: boolean;
  onFollowChange?: (isFollowing: boolean) => void;
}

export default function FollowButton({
  targetUserId,
  size = "sm",
  variant = "outline",
  className,
  showText = true,
  onFollowChange,
}: FollowButtonProps) {
  const { isAuthenticated, user } = useAuth();
  const [isHovering, setIsHovering] = useState(false);

  const { data: followStatus, isLoading: isCheckingFollow } = useQuery<{ isFollowing: boolean }>({
    queryKey: ['/api/follow', targetUserId],
    queryFn: async () => {
      const _tok = localStorage.getItem('auth_token');
      const response = await fetch(`/api/follow/${targetUserId}`, {
        headers: _tok && _tok !== 'null' && _tok !== 'undefined' ? { 'Authorization': `Bearer ${_tok}` } : {}
      });
      if (!response.ok) throw new Error('Failed to check follow status');
      return response.json();
    },
    enabled: isAuthenticated && !!targetUserId && 
      String(targetUserId) !== String(user?.id) && 
      (!user?.canonicalId || String(targetUserId) !== String(user.canonicalId)),
  });

  const followMutation = useMutation({
    mutationFn: async () => {
      return apiRequest("POST", "/api/follow", { followingId: targetUserId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/follow', targetUserId] });
      queryClient.invalidateQueries({ queryKey: ['/api/users', targetUserId, 'followers'] });
      queryClient.invalidateQueries({ queryKey: ['/api/user/profile'] });
      queryClient.invalidateQueries({ predicate: (query) => {
        const key = query.queryKey[0];
        return typeof key === 'string' && (key.includes('/following') || key.includes('/followers'));
      }});
      queryClient.invalidateQueries({ queryKey: ['/api/users/suggestions?limit=10'] });
      onFollowChange?.(true);
    },
  });

  const unfollowMutation = useMutation({
    mutationFn: async () => {
      return apiRequest("DELETE", `/api/follow/${targetUserId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/follow', targetUserId] });
      queryClient.invalidateQueries({ queryKey: ['/api/users', targetUserId, 'followers'] });
      queryClient.invalidateQueries({ queryKey: ['/api/user/profile'] });
      queryClient.invalidateQueries({ predicate: (query) => {
        const key = query.queryKey[0];
        return typeof key === 'string' && (key.includes('/following') || key.includes('/followers'));
      }});
      queryClient.invalidateQueries({ queryKey: ['/api/users/suggestions?limit=10'] });
      onFollowChange?.(false);
    },
  });

  const isSelf = !targetUserId || 
    String(targetUserId) === String(user?.id) || 
    (user?.canonicalId && String(targetUserId) === String(user.canonicalId));
  
  if (!isAuthenticated || isSelf) {
    return null;
  }

  const isFollowing = followStatus?.isFollowing ?? false;
  const isPending = followMutation.isPending || unfollowMutation.isPending;
  const isLoading = isCheckingFollow || isPending;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (isPending) return;

    if (isFollowing) {
      unfollowMutation.mutate();
    } else {
      followMutation.mutate();
    }
  };

  const getButtonContent = () => {
    if (isLoading) {
      return <Loader2 className="h-4 w-4 animate-spin" />;
    }

    if (isFollowing) {
      if (isHovering) {
        return (
          <>
            <UserMinus className="h-4 w-4" />
            {showText && <span>Unfollow</span>}
          </>
        );
      }
      return (
        <>
          <UserCheck className="h-4 w-4" />
          {showText && <span>Following</span>}
        </>
      );
    }

    return (
      <>
        <UserPlus className="h-4 w-4" />
        {showText && <span>Follow</span>}
      </>
    );
  };

  return (
    <Button
      size={size}
      variant={isFollowing ? (isHovering ? "destructive" : "secondary") : variant}
      className={cn(
        "gap-1 transition-colors",
        isFollowing && !isHovering && "bg-accent/50 text-accent-foreground",
        className
      )}
      onClick={handleClick}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      disabled={isLoading}
    >
      {getButtonContent()}
    </Button>
  );
}
